import { Form, Switch, Tooltip } from 'antd';
import classNames from 'classnames';
import { useMemo } from 'react';

import type { DAY_OF_WEEK } from 'app/typings/core/common/enum/dayOfWeek';
import { capitalizeFirstLetter } from 'app/utils/string';

import styles from './DailyConfigRow.module.scss';

export type FieldPath = (string | number)[];

type FormPaths = {
  isToggled: FieldPath;
  value: FieldPath;
  isDisabled: FieldPath;
};

export type DailyConfigRowProps = {
  day: DAY_OF_WEEK;
  basePath: FieldPath;
  disabledTooltip?: string;
  disabledText?: string;
  children?: (props: DailyConfigRowChildProps) => React.ReactNode;
  hasDisabledText?: boolean;
  fieldNames?: {
    isToggled?: string;
    value: string;
    isDisabled?: string;
  };
};

export type DailyConfigRowChildProps = {
  isToggled: boolean;
  currentValue: unknown;
  paths: FormPaths;
  day: DAY_OF_WEEK;
};

const buildDayFieldPaths = (
  basePath: FieldPath,
  day: DAY_OF_WEEK,
  fieldNames?: {
    isToggled?: string;
    value: string;
    isDisabled?: string;
  }
): FormPaths => ({
  isToggled: [...basePath, 'days', day, fieldNames?.isToggled ?? 'isToggled'],
  value: [...basePath, 'days', day, fieldNames?.value ?? 'stock'],
  isDisabled: [
    ...basePath,
    'days',
    day,
    fieldNames?.isDisabled ?? 'isDisabled',
  ],
});

export const DailyConfigRow = ({
  day,
  basePath,
  disabledTooltip = 'Configured as always closed by your account manager',
  disabledText: closedText = 'Closed',
  children,
  hasDisabledText: hasClosedText,
  fieldNames,
}: DailyConfigRowProps) => {
  const form = Form.useFormInstance();

  const paths = useMemo(
    () => buildDayFieldPaths(basePath, day, fieldNames),
    [basePath, day, fieldNames]
  );

  const isToggled = Form.useWatch(paths.isToggled, form);
  const currentValue = Form.useWatch(paths.value, form);
  const isDayDisabled = Form.useWatch(paths.isDisabled, form) ?? false;

  const childProps: DailyConfigRowChildProps = {
    isToggled,
    currentValue,
    paths,
    day,
  };

  return (
    <div
      className={classNames(styles.container, {
        [styles.disabledBackground]: isDayDisabled,
      })}
    >
      <div className={styles.inlineRow}>
        <div className={styles.leftPart}>
          {isDayDisabled ? (
            <Tooltip title={disabledTooltip} placement="top">
              <div>
                <Form.Item
                  name={paths.isToggled}
                  valuePropName="checked"
                  className={styles.inlineFormItem}
                  noStyle
                >
                  <Switch size="small" disabled={isDayDisabled} />
                </Form.Item>
              </div>
            </Tooltip>
          ) : (
            <div>
              <Form.Item
                name={paths.isToggled}
                valuePropName="checked"
                className={styles.inlineFormItem}
                noStyle
              >
                <Switch size="small" disabled={isDayDisabled} />
              </Form.Item>
            </div>
          )}

          <span>{capitalizeFirstLetter(day)}</span>
        </div>

        <div className={styles.rightPart}>
          <Form.Item name={paths.isDisabled} noStyle hidden>
            <input type="hidden" />
          </Form.Item>
          {!isToggled && hasClosedText && isDayDisabled && (
            <span className={styles.closed}>{closedText}</span>
          )}
        </div>
      </div>

      {isToggled && children && (
        <div className={styles.childBlock}>{children(childProps)}</div>
      )}
    </div>
  );
};
